import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  isConsentRequestPending,
  isRecordingConsentStepDone,
  parseUserSetupState,
  sessionRecordingExplainerVariant,
  sessionRecordingExplainerVersion,
  setupAudienceFor,
  setupRequirements,
  summarizeSetup,
  type SetupAudience,
  type SetupContext,
  type SetupFacts,
  type SetupRequirement,
  type SetupRow,
} from '@shared/setup'
import { useAuth } from '../../../contexts/auth-context'
import { useRecordingPolicyConsent } from '../../../stores/recording-policy-store'
import { useSessionFollowing, followsAnySessions } from '../../../stores/session-following-store'
import { useSetupProfile, type SetupProfileState } from '../../../stores/setup-profile-store'
import { getNativePlatform } from '../../../utils/platform'
import { usePremiumSessionRecording } from '../../session-recording/hooks/use-premium-session-recording'
import { probeDevice, type DeviceProbes } from '../data/setup-probes'
import { readLocalSetupState, type LocalSetupState } from '../data/setup-state'
import type { CopyContext } from '../data/setup-copy'
import { useCallsEnabled } from './use-calls-enabled'

export type SetupModel = {
  userId: string | null
  audience: SetupAudience | null
  /** All inputs are in (role, server profile, probes); setup may decide. */
  ready: boolean
  profile: SetupProfileState
  probes: DeviceProbes | null
  requirements: SetupRequirement[]
  rows: SetupRow[]
  facts: SetupFacts | null
  local: LocalSetupState
  consentRequestPending: boolean
  copy: CopyContext | null
  /** Re-run every device check (foreground, after an action). */
  refreshProbes: () => Promise<DeviceProbes | null>
  /** Re-read the local per-device state after a write. */
  refreshLocal: () => void
  /** Re-read account permissions (calling switched on/off by an admin). */
  refreshAccount: () => Promise<void>
}

/**
 * Everything setup decides from: the role, the server setup profile (consent
 * answer, admin requests, explainers), account flags, the follow summary and
 * the device checks.
 */
export function useSetupModel(): SetupModel {
  const { user } = useAuth()
  const userId = user?.userId ?? null
  const profile = useSetupProfile(userId)
  const consentStore = useRecordingPolicyConsent()
  const premiumSessionRecording = usePremiumSessionRecording()

  const role = useMemo(
    () => ({
      roleType: profile.profile?.roleType ?? user?.roleType ?? null,
      providerLevel: profile.profile?.providerLevel ?? user?.providerLevel ?? null,
    }),
    [profile.profile?.roleType, profile.profile?.providerLevel, user?.roleType, user?.providerLevel],
  )
  const audience = setupAudienceFor(role)
  const following = useSessionFollowing(audience === 'independent' ? userId : null)
  const followingSettled = audience !== 'independent' || following.status === 'loaded' || following.status === 'error'
  const [callsEnabled, refreshAccount] = useCallsEnabled(userId, audience)

  const [probes, setProbes] = useState<DeviceProbes | null>(null)
  const [local, setLocal] = useState<LocalSetupState>(() => readLocalSetupState(userId))
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const refreshProbes = useCallback(async () => {
    if (!userId) return null
    const next = await probeDevice()
    if (alive.current) setProbes(next)
    return next
  }, [userId])

  const refreshLocal = useCallback(() => setLocal(readLocalSetupState(userId)), [userId])

  useEffect(() => {
    setProbes(null)
    setLocal(readLocalSetupState(userId))
    if (userId) void refreshProbes()
  }, [userId, refreshProbes])

  const consentGiverFor = useMemo(
    () => (following.status === 'loaded' ? following.consentGiverFor : []),
    [following.status, following.consentGiverFor],
  )
  const consentGiver = consentGiverFor.length > 0

  const ctx: SetupContext | null = useMemo(() => {
    if (!audience || !probes || callsEnabled == null) return null
    return {
      role,
      platform: getNativePlatform(),
      callsEnabled,
      consentGiver,
      premiumSessionRecording,
      webPushSupported: probes.webPushSupported,
      nativeProbes: probes.nativeProbes,
      appLockSupported: probes.appLockSupported,
    }
  }, [audience, probes, callsEnabled, role, consentGiver, premiumSessionRecording])

  const requirements = useMemo(() => (ctx ? setupRequirements(ctx) : []), [ctx])

  const serverProfile = profile.status === 'loaded' ? profile.profile : null
  const consentRequestPending = isConsentRequestPending(serverProfile)

  const facts: SetupFacts | null = useMemo(() => {
    if (!ctx || !probes) return null
    // The server profile is the truth (it knows about admin requests); before it
    // loads, fall back to the decision the call gate already holds.
    const consentDone = serverProfile
      ? isRecordingConsentStepDone({ ...role, ...serverProfile })
      : consentStore.loaded
        ? isRecordingConsentStepDone({
            ...role,
            recordingPolicyConsentStatus: consentStore.status,
            recordingPolicyConsentAt: consentStore.acceptedAt,
            recordingPolicyConsentNoticeVersion: consentStore.noticeVersion,
          })
        : null
    const needed = sessionRecordingExplainerVersion(ctx)
    const seen = parseUserSetupState(serverProfile?.setupState).explainersSeen?.sessionRecording
    return {
      permissions: probes.permissions,
      recordingConsentDone: consentDone,
      sessionRecordingExplainerSeen: needed != null && seen === needed,
      appLockOn: probes.appLockOn,
    }
  }, [ctx, probes, serverProfile, role, consentStore])

  const rows = useMemo(
    () => (facts ? summarizeSetup(requirements, facts, local.optionalAsked).rows : []),
    [facts, requirements, local.optionalAsked],
  )

  const copy: CopyContext | null = useMemo(() => {
    if (!ctx || !audience || !probes) return null
    return {
      audience,
      platform: ctx.platform,
      sessionFollower: followsAnySessions(following),
      consentGiver,
      premiumSessionRecording,
      biometricLabel: probes.biometricLabel,
      explainerVariant: sessionRecordingExplainerVariant(ctx),
      consentGiverNames: consentGiverFor.map((c) => c.firstName),
    }
  }, [ctx, audience, probes, following, consentGiver, premiumSessionRecording, consentGiverFor])

  const ready =
    Boolean(userId && audience && ctx && facts) &&
    profile.status !== 'loading' &&
    profile.status !== 'idle' &&
    followingSettled

  return {
    userId,
    audience,
    ready,
    profile,
    probes,
    requirements,
    rows,
    facts,
    local,
    consentRequestPending,
    copy,
    refreshProbes,
    refreshLocal,
    refreshAccount,
  }
}
